import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {stringFilterMatches, truncateMiddle} from 'common/util/string';

import template from './index.jade';
import css from './index.styl?inline';

/**
 * @typedef {Object} Bookmark
 * @property {string} id
 * @property {string} user_id owner id
 * @property {string} user display name for the owner
 * @property {string} name
 * @property {string[]} tags
 * @property {Boolean} include_in_dashboard
 * @property {Boolean} is_default
 * @property {string} type report type
 * @property {string} created datetime as a string
 * @property {string} modified datetime as a string
 * @property {string} params JSON serialized object of bookmark params
 */

/**
 * Dropdown has been opened or closed.
 *
 * @event BookmarksWidget#change
 * @property {Boolean} details.open
 */

/**
 * A bookmark has been created, deleted or selected.
 *
 * @event BookmarksWidget#submit
 * @property {string} details.action=(select|create|delete) the action that occurred
 * @property {Bookmark} [details.value] selected bookmark (select only)
 * @property {string} [details.name] name of new bookmark (create only)
 * @property {string} [details.bookmarkId] id of deleted bookmark (delete only)
 */

/**
 * A dropdown to display and edit a list of bookmarks, grouped by owner.
 *
 * It supports select, create-new and delete operations. Side effects are
 * limited to emitted events.
 *
 * @fires BookmarksWidget#change
 * @fires BookmarksWidget#submit
 */
export class BookmarksWidget extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      bookmarks: {type: `json`, default: [], description: `bookmarks list of bookmarks to display`},
      'create-disabled': {type: `boolean`, description: `whether to allow creating new bookmarks`},
      'has-write-permissions': {type: `boolean`, description: `whether to allow create and delete`},
      'hide-create': {type: `boolean`, description: `whether to show the create button at all`},
      open: {type: `boolean`, description: `state of dropdown`},
      'over-free-limit': {type: `boolean`, description: `whether to display an upsell`},
      saving: {type: `boolean`, description: `whether to display saving spinner`},
      'selected-bookmark-id': {type: `number`, description: `ID of the active bookmark. It must exist in bookmarks`},
      'user-id': {type: `number`, description: `user whose bookmarks should show up under 'yours'`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        activeBookmark: null,
        bookmarks: [],
        confirmDeleteBookmarkId: null,
        createMode: false,
        filteredBookmarks: {},
        filterText: ``,
        name: ``,
        open: false,
        selectedBookmark: null,
        userId: null,
        showUpsellModal: false,
      },
      helpers: {
        toggleBookmarksMenu: () => this.setAttribute(`open`, !this.state.open),
        handleDropMenuChange: (ev) => {
          ev.stopImmediatePropagation();
          if (ev.detail.state === `open`) {
            this.el.querySelector(`.mp-bm-menu-search`).focus();
          } else if (ev.detail.state === `closed`) {
            this.setAttribute(`open`, false);
          }
        },
        handleSubmit: (ev) => {
          if (ev.detail.action === `select`) {
            this.setAttribute(`open`, false);
          }
        },
        setFilterText: (ev) => {
          this.update({filterText: ev.target.value});
          this._groupAndFilterBookmarks();
          this._setActiveBookmark(this._filteredBookmarksArray()[0]);
        },
        activateBookmark: (bookmark) => {
          this._setActiveBookmark(bookmark, false);
        },
        selectBookmark: (bookmark) => {
          this._selectBookmark(bookmark);
        },
        openConfirmDeleteDialog: (ev, bookmark) => {
          ev.stopImmediatePropagation();
          this.update({confirmDeleteBookmark: bookmark});
        },
        handleConfirmModalChange: (ev) => {
          ev.stopImmediatePropagation();
          if (ev.detail.state === `closed`) {
            this.update({confirmDeleteBookmark: null});
          }
        },
        handleConfirmSubmit: (ev) => {
          if (ev.detail.action === `confirm`) {
            this.dispatchEvent(
              new CustomEvent(`submit`, {
                detail: {
                  action: `delete`,
                  bookmarkId: this.state.confirmDeleteBookmark.id,
                },
              }),
            );
          }
        },
        goToCreateMode: () => {
          this.update({createMode: true});
          window.requestAnimationFrame(() => this.el.querySelector(`mp-input-group`).focus());
        },
        createBookmark: (ev) => {
          ev.stopImmediatePropagation();
          this.update({createMode: false});
          this.setAttribute(`saving`, true);
          this.dispatchEvent(new CustomEvent(`submit`, {detail: {action: `create`, name: this.state.name}}));
        },
        openUpsellModal: (ev) => {
          ev.stopPropagation();
          this.update({showUpsellModal: true});
        },
        closeUpsellModal: (ev) => {
          ev.stopPropagation();
          if (ev.detail.state === `closed`) {
            this.update({showUpsellModal: false});
          }
        },
        setName: (ev) => {
          ev.stopImmediatePropagation();
          this.update({name: ev.target.value});
        },
        stopPropagation: (ev) => {
          ev.stopImmediatePropagation();
        },
        stringFilterMatches,
        truncateMiddle,
      },
    };
  }

  _sortBookmarks(a, b) {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  }

  // put all default bookmarks at the top
  // followed by all bookmarks for the logged in "userId"
  // then sort by name
  _groupAndFilterBookmarks() {
    const groupedBookmarks = {
      'Default reports': [],
      'Your bookmarks': [],
      'Other bookmarks': [],
    };
    this.state.bookmarks
      .filter((b) => stringFilterMatches(b.name, this.state.filterText))
      .sort(this._sortBookmarks)
      .forEach((bookmark) => {
        if (bookmark.user_id === this.state.userId) {
          groupedBookmarks[bookmark.is_default ? `Default reports` : `Your bookmarks`].push(bookmark);
        } else if (!bookmark.is_default) {
          groupedBookmarks[`Other bookmarks`].push(bookmark);
        }
      });
    const filteredBookmarks = Object.entries(groupedBookmarks)
      .filter(([, bookmarks]) => bookmarks.length > 0)
      .reduce((acc, [sectionName, bookmarks]) => {
        acc[sectionName] = bookmarks;
        return acc;
      }, {});
    this.update({filteredBookmarks});
  }

  _filteredBookmarksArray() {
    // TODO use .flat() once it lands in Babel polyfill
    return [].concat(...Object.values(this.state.filteredBookmarks));
  }

  _setActiveBookmark(activeBookmark, scrollIntoView = true) {
    this.update({activeBookmark});

    if (scrollIntoView) {
      window.requestAnimationFrame(() => {
        const activeEl = this.el.querySelector(`mp-list-item[active]`);
        if (activeEl) {
          const listEl = this.el.querySelector(`.mp-bm-menu-list`);
          const viewportBottom = listEl.scrollTop + listEl.offsetHeight;
          const activeElBottom = activeEl.offsetTop + activeEl.offsetHeight;
          if (activeEl.offsetTop < listEl.scrollTop) {
            listEl.scrollTop = activeEl.offsetTop - 8;
          } else if (activeElBottom > viewportBottom) {
            listEl.scrollTop = activeElBottom - listEl.offsetHeight + 8;
          }
        }
      });
    }
  }

  _resetActiveBookmark() {
    const selectedBookmark = this.state.bookmarks.find((b) => b.id === this.attr(`selected-bookmark-id`));
    this._setActiveBookmark(selectedBookmark || this._filteredBookmarksArray()[0]);
  }

  _selectBookmark(selectedBookmark) {
    this.update({selectedBookmark});
    this.setAttribute(`open`, false);
    this.setAttribute(`selected-bookmark-id`, selectedBookmark.id);
    this.dispatchEvent(new CustomEvent(`submit`, {detail: {action: `select`, value: selectedBookmark}}));
  }

  _parseAttribute(name) {
    switch (name) {
      case `bookmarks`: {
        this.state.bookmarks = this.attr(`bookmarks`);
        this._groupAndFilterBookmarks();
        this._resetActiveBookmark();
        break;
      }
      case `open`: {
        const open = this.attr(`open`);
        this.dispatchEvent(new CustomEvent(`change`, {detail: {open}}));
        this.update({open});
        break;
      }
      case `saving`: {
        this.update({saving: this.attr(`saving`)});
        break;
      }
      case `selected-bookmark-id`: {
        this._resetActiveBookmark();
        break;
      }
      case `user-id`: {
        this.state.userId = this.attr(`user-id`);
        this._groupAndFilterBookmarks();
        this._resetActiveBookmark();
        break;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.localKeydownListener = (ev) => {
      if (this.state.open) {
        switch (ev.keyCode) {
          case 13: // enter
            this._selectBookmark(this.state.activeBookmark);
            break;
          case 38: {
            // up arrow
            ev.preventDefault();
            const bookmarks = this._filteredBookmarksArray();
            const activeIdx = bookmarks.indexOf(this.state.activeBookmark);
            if (activeIdx !== 0) {
              this._setActiveBookmark(bookmarks[activeIdx - 1]);
            }
            break;
          }
          case 40: {
            // down arrow
            ev.preventDefault();
            const bookmarks = this._filteredBookmarksArray();
            const activeIdx = bookmarks.indexOf(this.state.activeBookmark);
            if (activeIdx < bookmarks.length - 1) {
              this._setActiveBookmark(bookmarks[activeIdx + 1]);
            }
            break;
          }
        }
      }
    };
    this.addEventListener(`keydown`, this.localKeydownListener);

    // bootstrap the widget's state from attributes
    this.update({
      bookmarks: this.attr(`bookmarks`),
      open: this.attr(`open`),
      saving: this.attr(`saving`),
      userId: this.attr(`user-id`),
    });
    this._groupAndFilterBookmarks();
    if (this.state.open) {
      this._resetActiveBookmark();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    this.removeEventListener(`keydown`, this.localKeydownListener);
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    if (this.initialized) {
      this._parseAttribute(attr);
      if (attr === `open`) {
        this.update({createMode: false});
      }
    }
  }
}

export const BookmarksWidgetEl = defineAndMakeJsxFactory(`mp-bookmarks-widget`, BookmarksWidget);
